import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const API_URL = import.meta.env.VITE_API_URL;

export default function News() {
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  // Modal state for image viewing
  const [modalImage, setModalImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Pagination state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const loaderRef = useRef();

  // Fetch paginated news
  const fetchNews = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setIsFetchingMore(true);
      setError(null);
      setLoadingMessage(t('news.loading'));
      const res = await fetch(`${API_URL}/news?offset=${(pageNum - 1) * 10}&limit=10`);
      if (!res.ok) {
        if (res.status === 404) {
          setNews([]);
          setHasMore(false);
          setLoading(false);
          setIsFetchingMore(false);
          return;
        }
        throw new Error('Error fetching news');
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setNews(prev => append ? [...prev, ...data] : data);
        setHasMore(data.length === 10);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setError(err.message || 'Error');
      if (pageNum === 1) setNews([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setIsFetchingMore(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNews(1, false);
    setPage(1);
  }, [t, fetchNews]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || isFetchingMore) return;
    const observer = new window.IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        fetchNews(page + 1, true);
        setPage(p => p + 1);
      }
    }, { 
      threshold: 0.1,  // Trigger schon bei 10% Sichtbarkeit statt 100%
      rootMargin: '100px'  // 100px bevor Element sichtbar wird
    });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => { if (loaderRef.current) observer.unobserve(loaderRef.current); };
  }, [hasMore, loading, isFetchingMore, fetchNews, page]);

  const getFileUrl = (file) => {
    if (!file) return null;
    if (file.startsWith('http')) return file;
    return `${API_URL}${file}`;
  };

  // Helper to format date according to language
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (i18n.language.startsWith('de')) {
      return d.toLocaleDateString('de-DE');
    }
    return d.toLocaleDateString('en-US');
  };

  if (loading) return (
    <main className="flex-1 w-screen min-h-[400px] flex flex-col items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100 mb-4"></div>
      <p className="text-gray-700 dark:text-gray-300 text-center">{loadingMessage}</p>
    </main>
  );

  if (error) return (
    <main className="flex-1 w-screen min-h-[400px] flex items-center justify-center bg-gray-100 dark:bg-zinc-900">
      <div className="text-center text-red-500 dark:text-red-400 p-8">
        {t('error')}: {error}
      </div>
    </main>
  );

  return (
    <div className="min-h-screen w-screen bg-white dark:bg-zinc-900 transition-colors duration-300">
      <section className="w-full py-12 px-4 md:px-12 lg:px-24">
        <div className="w-full max-w-3xl mx-auto">
          <h1 className="text-4xl font-extrabold text-gray-900 dark:text-gray-100 mb-6 tracking-tight">
            {t('news.title')}
          </h1>
          <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-violet-500 rounded-full mb-10" />
          {(!news || news.length === 0) ? (
            <div className="text-center p-8">
              <p className="text-gray-500 mb-4 text-lg dark:text-gray-400">{t('news.no_news')}</p>
              <p className="text-sm text-gray-400">{t('news.add_first_news')}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              {news.map((n, idx) => (
                <article 
                  key={n.id} 
                  className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl border border-gray-200 dark:border-zinc-700 overflow-hidden group transition-all duration-300 hover:shadow-2xl"
                  style={{
                    animation: idx >= (page - 1) * 10 ? 'fadeInUp 0.5s ease-out forwards' : 'none'
                  }}>
                  {/* Date removed per user request */}
                  {/* Hero Image */}
                  {Array.isArray(n.images) && n.images.length > 0 && (
                    <div className="relative w-full bg-gray-100 dark:bg-zinc-900 flex items-center justify-center overflow-hidden">
                      <img
                        src={getFileUrl(n.images[0])}
                        alt="News Hero"
                        className="w-full h-auto max-h-[50vh] object-contain cursor-pointer"
                        loading="lazy"
                        onClick={() => { setModalImage(getFileUrl(n.images[0])); setIsModalOpen(true); }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent h-1/3" />
                    </div>
                  )}
                  <div className="p-6 md:p-8 flex flex-col gap-4">
                    {/* Gallery for additional images */}
                    {Array.isArray(n.images) && n.images.length > 1 && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-2">
                        {n.images.slice(1).map((img, idx) => (
                          <div key={idx} className="aspect-square relative overflow-hidden rounded-lg border shadow-sm">
                            <img 
                              src={getFileUrl(img)} 
                              alt="News Gallery" 
                              className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                              loading="lazy"
                              onClick={() => { setModalImage(getFileUrl(img)); setIsModalOpen(true); }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Text */}
                    <div className="mb-2 text-lg text-gray-800 dark:text-gray-100 whitespace-pre-line leading-relaxed font-serif">
                      {i18n.language.startsWith('de') ? n.text_de : n.text_en}
                    </div>
                    {/* Videos */}
                    {Array.isArray(n.videos) && n.videos.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {n.videos.map((vid, idx) => {
                          const videoUrl = getFileUrl(vid);
                          return (
                            <div key={idx} className="relative w-full bg-black rounded-xl overflow-hidden">
                              <div className="flex items-center justify-center">
                                <video 
                                  src={videoUrl}
                                  controls 
                                  className="h-auto"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {/* Loader for infinite scroll */}
              {hasMore && (
                <div ref={loaderRef} className="flex flex-col items-center justify-center my-8 gap-4">
                  {isFetchingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('news.loading_more', 'Weitere News werden geladen...')}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {news.length} {t('news.of_many', 'von vielen News geladen')}
                      </p>
                      <button
                        onClick={() => { fetchNews(page + 1, true); setPage(p => p + 1); }}
                        className="px-6 py-2 rounded-full bg-blue-500 text-white font-semibold shadow hover:bg-blue-600 transition"
                      >
                        {t('news.load_more', 'Mehr laden')}
                      </button>
                    </>
                  )}
                </div>
              )}
              {/* End message when no more news */}
              {!hasMore && news.length > 0 && (
                <div className="text-center my-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    {t('news.all_loaded', 'Alle News geladen')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      {/* Modal for full-size image */}
      {isModalOpen && modalImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90" onClick={() => setIsModalOpen(false)}>
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={modalImage}
              alt="Modal Preview"
              className="max-w-full max-h-full object-contain rounded shadow-lg"
              onClick={e => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 p-2 rounded border border-gray-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 shadow text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-400 z-[10000] text-2xl font-bold"
              onClick={e => { e.stopPropagation(); setIsModalOpen(false); }}
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 